import { BatteryState, ConnectedDeviceEnum, DeviceState, DeviceType, PanelType, WorkingMode } from '@web/ui-mobile';
import { ContactorMode, FlowNodeMode } from './enum/contactor.enum';
import { ElectricalDataKeys, StreamMeterFunction, StreamMeterMode } from './enum/stream-meter.enum';

export type BaseConnectedDevice = {
    id?: string;
    name?: string;
    serialNumber?: string;
    type: DeviceType;
    lastSynchronizationDate?: string;
    state?: boolean;
};

export type SolarPanel = BaseConnectedDevice & {
    type: PanelType;
    firmwareVersion?: string;
    batteryPreserveEnergy?: boolean;
    batteryThreshold?: number;
    wifiName?: string;
    rssiWifi?: number;
    bssid?: string;
    wifiIP?: string;
};

export type SolarPanelPatch = Omit<SolarPanel, 'wifiName' | 'type'>;

export enum FlowWorkingMode {
    POWER_OPTIM = 'POWER_OPTIM',
    COST_OPTIM = 'COST_OPTIM',
}

export type Hub = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.STREAM_CONNECT;
    hostname?: string;
    wifiName?: string;
    rssiWifi?: number;
    bssid?: string;
    wifiIP?: string;
    ethIP?: string;
    ethConnected?: boolean;
    connectedDevices?: BaseConnectedDevice[];
    firmwareVersion?: string;
    flowWorkingMode?: FlowWorkingMode;
};
export type HubPatch = Omit<Hub, 'type' | 'firmwareVersion'>;

export type ErlDevice = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.ERL | ConnectedDeviceEnum.ERL_GEN2;
    firmwareVersion?: string;
    state?: DeviceState;
    erlType?: DeviceType;
    isSellBack?: boolean;
};

export type ERL = ErlDevice & {
    type: ConnectedDeviceEnum.ERL;
};

export type ERL_GEN2 = ErlDevice & {
    type: ConnectedDeviceEnum.ERL_GEN2;
};

export type StreamMeter = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.STREAM_METER;
    firmwareVersion?: string;
    rssi?: number;
    electricalSystem?: StreamMeterMode;
    phasesConfiguration?: { [key in ElectricalDataKeys]: { phaseFunction: StreamMeterFunction } };
    pairingDate?: string;
    phaseXName?: string;
    phaseYName?: string;
    phaseZName?: string;
};
export type StreamMeterLight = Omit<
    StreamMeter,
    'firmwareVersion' | 'lastSynchronizedDate' | 'rssi' | 'status' | 'electricalSystem' | 'phasesConfiguration'
>;

export type StreamMeterPost = Omit<StreamMeter, 'phasesConfiguration'> & {
    phasesConfiguration?: { [key in ElectricalDataKeys]: StreamMeterFunction };
    phaseXName?: string;
    phaseYName?: string;
    phaseZName?: string;
};

export type PhaseConfiguration = {
    phasePurpose: StreamMeterFunction;
};

export type StorageBattery = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.STOREY;
    wifiName?: string;
    rssiWifi?: number;
    bssid?: string;
    wifiIP?: string;
    firmwareVersion?: string;
    connectedPacks?: number;
    batteryState?: BatteryState;
    workingMode?: WorkingMode;
    minSoc?: number;
    inhibitionTimeout?: Date;
    suncastManualConfigurations?: {
        priceName: string;
        priceAmount: number;
        batteryBehavior: 'CHARGE' | 'DISCHARGE';
        power: number;
    }[];
};
export type StorageBatteryPatch = Omit<StorageBattery, 'type' | 'firmwareVersion' | 'wifiName' | 'serialNumber'>;

export type Contactor = BaseConnectedDevice & {
    type: ConnectedDeviceEnum.LEGRAND_CONTACTOR;
    rssi: number;
    onOff: boolean;
    mode: ContactorMode;
    forcedOnEndDate?: string | null;
};

export type ContactorPost = Omit<Contactor, 'rssi' | 'onOff' | 'mode' | 'forcedOnEndDate'>;

export type ContactorPairing = ContactorPost & {
    hotWaterTankCapacity: number;
    hotWaterTankPower: number;
};

export type ContactorLight = Omit<Contactor, 'rssi' | 'onOff' | 'mode' | 'forcedOnEndDate'> & {
    pairingDate?: string;
};

export type ContactorPatch = {
    name?: string;
};

export type ContactorRuntimeParameters = {
    /**
     * Targeted minimum runtime throughout the day, in minutes. Read back from the hub, hence null when the hub
     * could not report it.
     */
    minOnTime?: number | null;
    /** Hot water tank capacity, in liters. */
    hotWaterTankCapacity?: number | null;
    /** Hot water tank power, in watts. */
    hotWaterTankPower?: number | null;
};

/** Data returned by backend once mode change is processed. */
export type ContactorModeUpdate = {
    id: string;
    serialNumber: string;
    mode: FlowNodeMode;
    forcedOnEndDate?: string | null;
};

export type ConnectedDevice =
    | SolarPanel
    | Hub
    | StorageBattery
    | ERL
    | ERL_GEN2
    | StreamMeter
    | StreamMeterLight
    | Contactor
    | ContactorLight;
export type ConnectedDevicePost = SolarPanel | Hub | StorageBattery | ERL | ERL_GEN2 | StreamMeterPost | ContactorPost;
